import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { saveAs } from "file-saver";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const SellersPage = () => {
  const [sellerData, setSellerData] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  // 1. Add 'city' to the formData state
  const [formData, setFormData] = useState({
    sellerEmail: "",
    sellerCode: "", 
    city: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editSellerId, setEditSellerId] = useState(null);

  const navigate = useNavigate();
  const columnHelper = createColumnHelper();

  const columns = useMemo(
      () => [
        columnHelper.accessor("sellerEmail", {
          header: () => "STL Email",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("sellerCode", {
          header: () => "STL Name",
          cell: (info) => info.getValue(),
        }),
        // 2. Add a new column for 'City'
        columnHelper.accessor("city", {
          header: () => "City",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("actions", {
          header: () => "Actions",
          cell: (info) =>
              info.row.original.channelManagerID ? (
                  <div className="">
                    <button
                        onClick={() => handleEdit(info.row.original)}
                        className="btn btn-primary btn-xs"
                    >
                      Edit
                    </button>
                    <button
                        onClick={() => handleDelete(info.row.original._id)}
                        className="btn btn-error btn-xs"
                    >
                      Delete
                    </button>
                  </div>
              ) : (
                  <div className=" font-bold text-red-500">
                    <span>Created By Admin !!</span>
                  </div>
              ),
        }),
      ],
      []
  );

  const table = useReactTable({
    data: sellerData,
    columns,
    state: {
      globalFilter: searchFilter,
    },
    onGlobalFilterChange: setSearchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getSellerList = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      const response = await axios.get(
          `/sellers`,
          { headers }
      );
      setSellerData(response.data);
    } catch (error) {
      console.error("Error fetching seller data:", error);
      toast.error("Error fetching seller data.");
    }
  };

  const handleSubmit = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      if (isEditMode) {
        // Update existing seller
        await axios.put(
            `/sellers/${editSellerId}`,
            formData, // formData now includes city
            { headers }
        );
        toast.success("Seller updated successfully!");
      } else {
        // Create new seller
        await axios.post(
            `/sellers`,
            formData, // formData now includes city
            { headers }
        );
        toast.success("Seller created successfully!");
      }
      getSellerList();
      resetForm();
    } catch (error) {
      console.error("Error creating/updating seller:", error);
      toast.error(`Failed to ${isEditMode ? "update" : "create"} seller.`);
    }
  };

  const handleEdit = (seller) => {
    setIsEditMode(true);
    setEditSellerId(seller._id);
    // 3. Populate the form with city data on edit
    setFormData({
      sellerEmail: seller.sellerEmail,
      sellerCode: seller.sellerCode,
      city: seller.city || "", // Use empty string as fallback
    });
    document.getElementById("addSellerModel").showModal();
  };

  const handleDelete = async (sellerId) => {
    if (!window.confirm("Are you sure you want to delete this seller?")) {
      return;
    }

    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      await axios.delete(
          `/sellers/${sellerId}`,
          { headers }
      );
      toast.success("Seller deleted successfully!");
      getSellerList();
    } catch (error) {
      console.error("Error deleting seller:", error);
      toast.error("Failed to delete seller.");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      toast.error("Please select a CSV file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const csvText = e.target.result;
      const lines = csvText.split("\n");
      const headers = lines[0].split(",").map((header) => header.trim());

      for (let i = 1; i < lines.length; i++) {
        const data = lines[i].split(",").map((item) => item.trim());
        if (data.length !== headers.length) {
          console.warn(
              `Skipping line ${i + 1} due to incorrect number of columns.`
          );
          continue;
        }

        const sellerData = {};
        for (let j = 0; j < headers.length; j++) {
          sellerData[headers[j]] = data[j];
        }

        if (sellerData.sellerEmail && sellerData.sellerCode) {
          try {
            const userToken = localStorage.getItem("user-token");
            const headers = { authorization: userToken };
            await axios.post(
                `/sellers`,
                {
                  sellerEmail: sellerData.sellerEmail,
                  sellerCode: sellerData.sellerCode,
                  city: sellerData.city || "", // Handle city from CSV
                },
                { headers }
            );
            toast.success(
                `Seller ${sellerData.sellerEmail} created successfully!`
            );
          } catch (error) {
            console.error("Error creating STL:", error);
            toast.error(
                `Failed to create STL ${sellerData.sellerEmail}.`
            );
          }
        } else {
          toast.error(`Please add sellerEmail and sellerCode to row ${i + 1}`);
        }
      }
      getSellerList();
    };
    reader.onerror = () => {
      toast.error("Error reading CSV file.");
    };
    reader.readAsText(file);
  };

  const resetForm = () => {
    // 4. Reset the city field
    setFormData({ sellerEmail: "", sellerCode: "", city: "" });
    setIsEditMode(false);
    setEditSellerId(null);
    document.getElementById("addSellerModel").close();
  };



  useEffect(() => {
    getSellerList();
  }, [searchFilter]);

  const downloadCSV = () => {
    if (!sellerData || sellerData.length === 0) {
      alert("No data to download.");
      return;
    }

    const headers = Object.keys(sellerData[0]); // Get headers from the first object
    const csvRows = [];

    csvRows.push(headers.join(",")); // Add headers

    for (const row of sellerData) {
      const values = headers.map((header) => {
        let value = row[header];
        if (value === null || value === undefined) {
          value = "";
        }

        // Escape commas and quotes
        let processedValue = String(value).replace(/"/g, '""');
        if (processedValue.includes(",") || processedValue.includes('"')) {
          processedValue = `"${processedValue}"`;
        }
        return processedValue;
      });
      csvRows.push(values.join(","));
    }

    const csvData = csvRows.join("\n");

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "outlets.csv");
  };

  return (
      <div className={"flex"}>
        
        <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold mb-4">STL</h1>
            <div>
              <button
                  onClick={() =>
                      document.getElementById("addSellerModel").showModal()
                  }
                  className="btn btn-primary mr-2"
                  style={{ background: "limegreen", color: "white" }}
              >
                <span className="text-bold text-xl"> + </span> Add STL
              </button>
              <button onClick={downloadCSV} className="btn btn-primary">
                <span className="text-bold text-xl"> ↯ </span> Download CSV
              </button>
            </div>
          </div>

          {/* Creation/Edit Form */}
          <dialog id="addSellerModel" className="modal">
            <div className="modal-box flex flex-col m-auto items-center p-6 rounded-lg shadow-md bg-white w-full max-w-2xl">
              <h2 className="text-lg font-semibold mb-4 text-gray-800">
                {isEditMode ? "Edit STL" : "Create STL"}
              </h2>
              <div className="w-full mb-4">
                <input
                    type="email"
                    placeholder="STL Email"
                    className="input input-bordered w-full mb-2"
                    value={formData.sellerEmail}
                    onChange={(e) =>
                        setFormData({ ...formData, sellerEmail: e.target.value })
                    }
                />
                <input
                    type="text"
                    placeholder="STL Name"
                    className="input input-bordered w-full mb-2"
                    value={formData.sellerCode}
                    onChange={(e) =>
                        setFormData({ ...formData, sellerCode: e.target.value })
                    }
                />
                {/* 5. Add the City text field to the form */}
                <input
                    type="text"
                    placeholder="City"
                    className="input input-bordered w-full mb-2"
                    value={formData.city}
                    onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                    }
                />
              </div>
              <div className="flex justify-center w-full mb-4 space-x-2">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  {isEditMode ? "Update" : "Create"}
                </button>

                <button
                    className="btn btn-secondary"
                    onClick={() => {
                      resetForm();
                    }}
                >
                  Cancel
                </button>
              </div>

              {/*<div className="w-full flex flex-col items-center">*/}
              {/*    <div className="mb-2">*/}
              {/*        <input type="file" className="file-input file-input-bordered file-input-xs"*/}
              {/*               onChange={handleFileUpload}/>*/}
              {/*    </div>*/}
              {/*    <div>*/}
              {/*        <a href="/sample_sellers.csv" download="sample_sellers.csv"*/}
              {/*           className="text-sm text-blue-500 hover:underline">Download Sample CSV</a>*/}
              {/*    </div>*/}
              {/*</div>*/}
            </div>
          </dialog>

          <div className="p-4">
            <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search by STL Email or Code..."
                className="input input-bordered w-full"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                        <th
                            key={header.id}
                            className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                header.column.getCanSort()
                                    ? "cursor-pointer select-none"
                                    : ""
                            }`}
                            onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                              )}
                          {{
                            asc: " ↑",
                            desc: " ↓",
                          }[header.column.getIsSorted()] ?? null}
                        </th>
                    ))}
                  </tr>
              ))}
              </thead>
              <tbody>
              {table.getRowModel().rows.map((row) => (
                  <tr
                      key={row.id}
                      className="hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                  >
                    {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                          )}
                        </td>
                    ))}
                  </tr>
              ))}
              </tbody>
            </table>
          </div>

          <div className="join p-4">
            <button
                className="join-item btn"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
            >
              {"<<"}
            </button>
            <button
                className="join-item btn"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
            >
              {"<"}
            </button>
            <button
                className="join-item btn"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
            >
              {">"}
            </button>
            <button
                className="join-item btn"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
            >
              {">>"}
            </button>

            <span
                style={{ alignItems: "center" }}
                className="flex input input-bordered join-item"
            >
            Page{" "}
              <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
            </strong>
          </span>
            <span className="flex items-center gap-1">
            <input
                type="number"
                min={1}
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                className="input input-bordered w-24"
            />
          </span>
            <select
                className="select select-bordered"
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
              ))}
            </select>
          </div>
        </div>
      </div>
  );
};

export default SellersPage;
